<script setup lang="ts">
import { NLayoutSider, NMenu } from 'naive-ui'
import { computed, nextTick, onMounted, watch } from 'vue'
import Footer from './FooterClassroom.vue'
// import logoDark from '@/assets/classroom-v2/nextSchoolDark.png'
// import logoLight from '@/assets/classroom-v2/nextSchool.png'
import logoDark from '@/assets/login/logoLoginDark.png'
import logoLight from '@/assets/login/logoLoginLight.png'
import logoCollapsed from '@/assets/login/logoIcon.png'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { useTheme } from '@/hooks/useTheme'
import { useSiderMenuTeacher } from '@/components/layout/sider/composables/useSiderMenu'
import { useAppStore, useClassroomStore, useUserStore } from '@/store'
import { useNavigation } from '@/components/layout/sider/composables'

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
  isDrawer: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:collapsed', 'menu-click'])

const { isMobile, isTablet } = useBasicLayout()
const { isDark } = useTheme()
const userStore = useUserStore()
const classroomStore = useClassroomStore()
const appStore = useAppStore()
const {
  collapsed,
  selectedKeyClassroom,
  handleSelect,
  handleUpdateCollapsed,
} = useNavigation()

// const internalCollapsed = ref(props.collapsed)
//
// watch(() => props.collapsed, (val) => {
//   internalCollapsed.value = val
// })
//
// const collapsed = computed(props.collapsed)

const { listMenu, expandedKeys } = useSiderMenuTeacher(collapsed)

const handleMenuSelect = (key: string) => {
  handleSelect(key)
  if (props.isDrawer)
    emit('menu-click')
}

const workSpaceId = computed(() => userStore.workSpaceId)
// const handleUpdateCollapsed = (value: boolean) => {
//   collapsed.value = value
// }

const syncCollapsedWithViewport = () => {
  const shouldCollapse = isMobile.value || isTablet.value
  if (shouldCollapse !== props.collapsed)
    appStore.setSiderCollapsed(true)
}

watch([isMobile, isTablet], () => {
  syncCollapsedWithViewport()
}, { immediate: true })

// Load danh sách classroom khi component mount
onMounted(async () => {
  if (workSpaceId.value && classroomStore.getClassrooms.length === 0) {
    try {
      await classroomStore.fetchClassrooms(workSpaceId.value)
    }
    catch (error) {
      console.error('Error loading classrooms in sidebar:', error)
    }
  }

  // Sync collapsed state immediately on mount after other setup
  nextTick(() => {
    syncCollapsedWithViewport()
  })
})
</script>

<template>
  <!-- Drawer Mode (Mobile/Tablet) -->
  <div v-if="isDrawer" class="flex flex-col h-full bg-white dark:bg-[#1e1e2d]">
    <!-- Main Content -->
    <main class="flex-1 min-h-0 flex flex-col">
      <!-- Navigation Menu -->
      <nav class="flex-1 overflow-auto px-2">
        <NMenu
          :options="listMenu"
          :collapsed="false"
          :value="selectedKeyClassroom"
          :indent="16"
          :icon-size="20"
          :expand-icon="() => null"
          :default-expanded-keys="expandedKeys"
          class="teacher-menu" :class="[selectedKeyClassroom === 'classroom.teacher.board' ? 'custom-teach-board' : '']"
          @update:value="handleMenuSelect"
        />
      </nav>
    </main>
    <Footer :collapsed="false" :is-drawer="true" class="flex-shrink-0" />
  </div>

  <!-- Desktop Sider Mode -->
  <NLayoutSider
    v-else
    :collapsed="collapsed"
    :collapsed-width="70"
    :width="260"
    :show-trigger="!isMobile && 'arrow-circle'"
    collapse-mode="width"
    position="absolute"
    bordered
    class="transition-all duration-300 ease-in-out shadow-lg desktop-sider"
    @update-collapsed="handleUpdateCollapsed"
  >
    <div class="flex flex-col h-full bg-white dark:bg-[#1e1e2d]">
      <!-- Header with Logo -->
      <header class="flex items-center" :class="{ 'justify-center px-2': collapsed, 'px-6': !collapsed }">
        <RouterLink to="/agent-management" class="flex items-center py-4">
          <img
            v-if="!collapsed"
            :src="isDark ? logoDark : logoLight"
            alt="Logo"
          >
          <img
            v-else
            :src="logoCollapsed"
            class="w-10"
            alt="Logo Collapsed"
          >
        </RouterLink>
      </header>

      <!-- Main Content -->
      <main class="flex-1 min-h-0 flex flex-col">
        <!-- Navigation Menu -->
        <nav class="flex-1 overflow-auto px-2">
          <NMenu
            :options="listMenu"
            :collapsed="collapsed"
            :collapsed-width="52"
            :value="selectedKeyClassroom"
            :indent="16"
            :icon-size="20"
            :collapsed-icon-size="20"
            :expand-icon="() => null"
            :default-expanded-keys="expandedKeys"
            class="teacher-menu" :class="[selectedKeyClassroom === 'classroom.teacher.board' ? 'custom-teach-board' : '']"
            @update:value="handleSelect"
          />
        </nav>
      </main>
      <Footer :collapsed="collapsed" class="flex-shrink-0" />
    </div>
  </NLayoutSider>
</template>

<style>
.desktop-sider {
  position: fixed !important;
  height: var(--app-height, 100vh);
}
</style>

<style scoped>
:deep(.n-submenu-children .n-menu-item-content) {
	/* deeper indent for submenu items */
	padding-left: 43px !important;
}
:deep(.n-menu-item-content) {
	padding-right: 16px;
	font-weight: 400;
	font-size: 14px;
	opacity: 0.9;
	color: #666666 !important;
}

:deep(.popover-menu-wrapper .n-menu) {
	background: transparent;
}

:deep(.popover-menu-wrapper .n-menu-item) {
	padding: 8px 12px;
	margin: 2px 0;
	border-radius: 6px;
	transition: all 0.2s ease;
}

:deep(.popover-menu-wrapper .n-menu-item:hover) {
	background-color: #f5f5f5;
	transform: translateY(-1px);
}

:deep(.tool-group-title) {
	font-size: 12px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 8px;
	padding: 4px 8px;
}

/* Vertical guideline for submenu children */
:deep(.n-submenu-children) {
	position: relative;
}
:deep(.n-submenu-children::before) {
	content: '';
	position: absolute;
	left: 25px; /* align with deeper indent */
	top: 6px;
	bottom: 6px;
	width: 1px;
}

:deep(.n-menu .n-menu-item-content .n-menu-item-content__icon) {
	color: #6A5B70;
}
:deep(.custom-teach-board.n-menu .n-menu-item-content.n-menu-item-content--selected .n-menu-item-content__icon .enterprise-icon > div) {
background:linear-gradient(180deg, #F96 0%, #C6F 100%);
-webkit-text-fill-color: transparent!important;
}

:deep(.n-menu .n-menu-item-content:not(.n-menu-item-content--disabled):hover .n-menu-item-content__icon)
{
	color: #6A5B70;
}

/* Active styles customization for Teacher sider */
/* Parent submenu (this item) selected OR has active child -> show active background */
:deep(.teacher-menu.n-menu) {
  /* set active background for all items (we will cancel for children below) */
  --n-item-color-active: #F3E8FF; /* purple-100 */
  --n-item-color-active-hover: #F3E8FF;
}
:deep(.teacher-menu.n-menu .n-submenu > .n-menu-item-content.n-menu-item-content--selected),
:deep(.teacher-menu.n-menu .n-submenu.n-submenu--child-active > .n-menu-item-content) {
  color: #5B21B6 !important; /* purple-800 */
  border-radius: 8px;
  background-color: #F3E8FF !important; /* ensure visible even if overlay not applied */
}
:deep(.teacher-menu.n-menu .n-submenu > .n-menu-item-content.n-menu-item-content--selected)::before,
:deep(.teacher-menu.n-menu .n-submenu.n-submenu--child-active > .n-menu-item-content)::before,
:deep(.teacher-menu.n-menu .n-submenu > .n-menu-item-content .n-menu-item-content__state),
:deep(.teacher-menu.n-menu .n-submenu.n-submenu--child-active > .n-menu-item-content .n-menu-item-content__state) {
  background-color: #F3E8FF !important; /* purple-100 */
  opacity: 1 !important;
}
/* Child menu items: never show active background, only color when selected */
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item-content) {
  --n-item-color-active: transparent !important;
  --n-item-color-active-hover: transparent !important;
}
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item-content::before),
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item-content .n-menu-item-content__state) {
  background-color: transparent !important;
}
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item-content.n-menu-item-content--selected::before),
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item-content.n-menu-item-content--selected .n-menu-item-content__state) {
  background-color: transparent !important;
  opacity: 0 !important;
}
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item-content.n-menu-item-content--selected) {
  background-color: transparent !important;
  color: #5B21B6 !important;
  border-radius: 6px;
}
/* Force remove any tint/overlay on child items (hover/active/selected) */
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item .n-menu-item-content:hover::before),
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item .n-menu-item-content.n-menu-item-content--selected::before),
:deep(.teacher-menu.n-menu .n-submenu-children .n-menu-item .n-menu-item-content:active::before) {
  background-color: transparent !important;
}
</style>

<style>
/* Light mode */
.n-submenu-children::before {
  background-color: #E0E0E0;
}

/* Dark mode */
.dark .n-submenu-children::before {
  background-color: #2C2C2C;
}
</style>
